import classNames from 'classnames'
import { useCallback, useState } from 'react'
import { useSlate } from 'slate-react'

import { Button } from 'core-components/library/button'
import { IFileSettings } from 'core-components/library/dragNDropFileUploader'
import { Flex } from 'core-components/library/flex'
import { NumberInput } from 'core-components/library/inputs'
import { LoaderOverlay } from 'core-components/library/loader'
import { TVideo } from 'core-components/library/richTextEditor/interface/TVideo'
import { Controller, ValidationStatus } from 'core-components/library/validation'

import { EMediaType } from '../common/mediaDragNDrop/interfaces'
import { MediaDragNDrop } from '../common/mediaDragNDrop/MediaDragNDrop'

import styles from './VideoSettingsDialog.module.scss'

interface IProps {
  videoSettings: TVideo
  onCancel: () => void
  onSave: (iframeSettings: TVideo) => void
}

export const VideoSettingsDialog = ({ videoSettings, onSave, onCancel }: IProps) => {
  const [canSave, setCanSave] = useState(false)
  const [editedSettings, setEditedSetting] = useState({
    ...videoSettings,
  })
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [file, setFile] = useState<IFileSettings | null | undefined>(undefined)

  const { fileUploadAdapter } = useSlate()

  const updateSettings = useCallback((field: keyof TVideo, value: string) => {
    setEditedSetting((prevState) => ({ ...prevState, [field]: value }))
  }, [])

  const onFileLoad = useCallback(
    async (file: IFileSettings) => {
      if (fileUploadAdapter) {
        return fileUploadAdapter(file)
      }
      return ''
    },
    [fileUploadAdapter]
  )

  const onSaveClick = useCallback(async () => {
    setIsLoading(true)
    if (file) {
      const url = await onFileLoad(file)
      if (url) {
        updateSettings('url', url)
        onSave({ ...editedSettings, url: url })
      }
    } else {
      onSave(editedSettings)
    }
    setIsLoading(false)
  }, [file, onFileLoad, updateSettings, onSave, editedSettings])

  return (
    <Controller
      onValidationStatusChange={(result) => {
        setCanSave(result.status === ValidationStatus.Valid)
      }}
    >
      <LoaderOverlay show={isLoading} />
      <Flex
        alignItems={'baseline'}
        justifyContent={'space-between'}
        width={'100%'}
        className={classNames('margin-bottom-sm', 'margin-top-sm')}
      >
        <div>Height:</div>
        <NumberInput
          classes={{ root: styles.settingsLine__value }}
          label={'height'}
          value={editedSettings.height || ''}
          onChange={(value) => updateSettings('height', value)}
          endContent={<div>px</div>}
        />
      </Flex>

      <MediaDragNDrop
        onUrlChange={(value) => updateSettings('url', value)}
        fileUrl={editedSettings.url || ''}
        type={EMediaType.Video}
        onFileChange={(value) => setFile(value)}
      />
      <div className={styles.buttons}>
        <Button className={styles.button} disabled={!canSave} onClick={onSaveClick}>
          Save
        </Button>
        <Button className={styles.button} onClick={() => onCancel()}>
          Cancel
        </Button>
      </div>
    </Controller>
  )
}
